export interface TutorialFarm {
  id: string;
  name: string;
  location: string;
  size: number;
  status: string;
  description: string;
  crops: string[];
  imageUrl: string;
  tutorialModules?: {
    tasks: {
      summary: string;
      workers: Array<{
        id: string;
        name: string;
        role: string;
        specialization: string[];
      }>;
      tasks: Array<{
        id: string;
        title: string;
        description: string;
        category: string;
        priority: 'low' | 'medium' | 'high' | 'urgent';
        status: 'pending' | 'in-progress' | 'completed' | 'on-hold';
        frequency: 'daily' | 'weekly' | 'monthly';
        timeSlots?: string[];
        isMandatory: boolean;
        dueDate: string;
        assignedTo: Array<{
          id: string;
          name: string;
          avatar?: string;
        }>;
        checklist: Array<{
          id: string;
          text: string;
          completed: boolean;
        }>;
        notes: string;
        createdAt: string;
        updatedAt: string;
        assignedWorkers: string[];
        completedDate?: string;
      }>;
    };
    weather: {
      summary: string;
      planningNotes: string[];
      cropRecommendations: Array<{
        name: string;
        suitability: 'high' | 'medium' | 'low';
        notes: string;
      }>;
      pestAlerts: Array<{
        name: string;
        risk: 'high' | 'medium' | 'low';
        crops: string[];
        prevention: string;
      }>;
    };
    produce: {
      summary: string;
      harvestTargets: Array<{
        name: string;
        unit: string;
        target: string;
        notes: string;
      }>;
      marketingChecklist: string[];
    };
    workforce: {
      summary: string;
      playbook: string[];
    };
  };
}

const daysFromNow = (days: number) => new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
const daysAgo = (days: number) => new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

export const tutorialFarms: TutorialFarm[] = [
  {
    id: 'tutorial-green-valley',
    name: 'Green Valley Farm',
    location: 'Nakuru County',
    imageUrl:
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1489&q=80',
    size: 10,
    status: 'active',
    description: 'Tutorial example showing a mixed-crop farm setup for onboarding guidance.',
    crops: ['Maize', 'Beans'],
    tutorialModules: {
      tasks: {
        summary: 'This tutorial task board shows how recurring fieldwork, irrigation checks, and harvest preparation can be structured before you create real farm tasks.',
        workers: [
          {
            id: 'tutorial-worker-jane',
            name: 'Jane K.',
            role: 'Field Supervisor',
            specialization: ['Crop Management', 'Irrigation', 'Harvest Planning'],
          },
          {
            id: 'tutorial-worker-peter',
            name: 'Peter M.',
            role: 'Farm Hand',
            specialization: ['Weeding', 'Spraying', 'Post-Harvest Handling'],
          },
        ],
        tasks: [
          {
            id: 'tutorial-task-irrigation',
            title: 'Inspect drip lines before the next watering cycle',
            description: 'Walk the maize and bean blocks, clear blocked emitters, and note pressure drops before irrigation starts.',
            category: 'Infrastructure',
            priority: 'high',
            status: 'pending',
            frequency: 'weekly',
            isMandatory: true,
            dueDate: daysFromNow(1),
            assignedTo: [],
            checklist: [
              { id: 'tutorial-irrigation-1', text: 'Check pressure at the main line', completed: false },
              { id: 'tutorial-irrigation-2', text: 'Inspect emitters in the maize block', completed: false },
              { id: 'tutorial-irrigation-3', text: 'Record any leaks for repair', completed: false },
            ],
            notes: 'Tutorial sample only. Use the same structure on a real farm to create assignable maintenance routines.',
            createdAt: daysAgo(2),
            updatedAt: daysAgo(1),
            assignedWorkers: ['tutorial-worker-jane'],
          },
          {
            id: 'tutorial-task-weeding',
            title: 'Complete second-round weeding in the bean section',
            description: 'Remove competing weeds before moisture stress increases and document any low-growth patches for follow-up.',
            category: 'Crop Management',
            priority: 'medium',
            status: 'in-progress',
            frequency: 'weekly',
            timeSlots: ['08:00', '14:00'],
            isMandatory: true,
            dueDate: daysFromNow(2),
            assignedTo: [],
            checklist: [
              { id: 'tutorial-weeding-1', text: 'Clear row edges', completed: true },
              { id: 'tutorial-weeding-2', text: 'Inspect for pest pressure after weeding', completed: false },
            ],
            notes: 'This sample shows how progress tracking can be attached to a field operation.',
            createdAt: daysAgo(3),
            updatedAt: daysAgo(1),
            assignedWorkers: ['tutorial-worker-peter'],
          },
          {
            id: 'tutorial-task-harvest',
            title: 'Prepare storage area for early harvest lots',
            description: 'Clean sacks, confirm moisture checks are ready, and separate produce by quality grade before dispatch.',
            category: 'General',
            priority: 'urgent',
            status: 'completed',
            frequency: 'monthly',
            isMandatory: true,
            dueDate: daysAgo(1),
            assignedTo: [],
            checklist: [
              { id: 'tutorial-harvest-1', text: 'Clean produce storage shelves', completed: true },
              { id: 'tutorial-harvest-2', text: 'Prepare moisture meter', completed: true },
            ],
            notes: 'Completed tutorial example for post-harvest readiness.',
            createdAt: daysAgo(6),
            updatedAt: daysAgo(1),
            assignedWorkers: ['tutorial-worker-jane', 'tutorial-worker-peter'],
            completedDate: daysAgo(1).split('T')[0],
          },
        ],
      },
      weather: {
        summary: 'This tutorial weather plan focuses on how a mixed maize and bean farm in Nakuru can turn weather information into planting, irrigation, and pest-monitoring actions.',
        planningNotes: [
          'Review rainfall timing before top-dressing maize so nutrients are not lost to runoff.',
          'Use bean field checks after humid mornings to catch fungal pressure early.',
          'Translate forecast changes into irrigation and labor scheduling rather than relying on raw weather numbers alone.',
        ],
        cropRecommendations: [
          {
            name: 'Maize',
            suitability: 'high',
            notes: 'Good fit for the tutorial farm profile when field drainage and timely fertilizer application are managed well.',
          },
          {
            name: 'Beans',
            suitability: 'high',
            notes: 'Works well as a companion crop when scouting for fungal issues after wet spells is kept consistent.',
          },
          {
            name: 'Tomatoes',
            suitability: 'medium',
            notes: 'Possible for a small horticulture block, but requires tighter disease monitoring than the tutorial staple crops.',
          },
        ],
        pestAlerts: [
          {
            name: 'Fall Armyworm',
            risk: 'high',
            crops: ['Maize'],
            prevention: 'Scout whorls weekly and isolate affected sections before the infestation spreads across the block.',
          },
          {
            name: 'Bean Rust',
            risk: 'medium',
            crops: ['Beans'],
            prevention: 'Monitor after humid weather, keep spacing open, and remove heavily affected plant material quickly.',
          },
        ],
      },
      produce: {
        summary: 'This tutorial produce plan shows how harvest goals, grading, and market preparation can be structured before you start recording real batch and marketplace data.',
        harvestTargets: [
          {
            name: 'Dry Maize',
            unit: 'kg',
            target: '3,200',
            notes: 'Store only after moisture checks are complete and separate damaged grain before sale.',
          },
          {
            name: 'Beans',
            unit: 'kg',
            target: '1,150',
            notes: 'Grade by size and dryness so traders can price consistent lots more easily.',
          },
        ],
        marketingChecklist: [
          'Grade produce before transport so buyers see a consistent lot.',
          'Record harvest date, unit, and available quantity before posting to the marketplace.',
          'Separate household retention stock from commercial stock before pricing produce.',
        ],
      },
      workforce: {
        summary: 'This tutorial workforce view explains how a small mixed-crop farm can assign responsibilities before live attendance, payroll, and compliance records are connected.',
        playbook: [
          'Assign one supervisor to irrigation and crop inspections for accountability.',
          'Rotate repetitive labor like weeding and sorting to avoid task bottlenecks.',
          'Link worker tasks to the crop calendar so labor demand is easier to forecast.',
        ],
      },
    },
  },
  {
    id: 'tutorial-sunrise-dairy',
    name: 'Sunrise Dairy Farm',
    location: 'Kiambu County',
    imageUrl:
      'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1489&q=80',
    size: 15,
    status: 'active',
    description: 'Tutorial example showing how livestock-oriented farm records can be structured.',
    crops: ['Napier Grass', 'Fodder Maize'],
    tutorialModules: {
      tasks: {
        summary: 'This tutorial task board shows how a dairy-oriented farm can organize feeding, hygiene, fodder, and milk handling routines without touching live records.',
        workers: [
          {
            id: 'tutorial-worker-mary',
            name: 'Mary W.',
            role: 'Dairy Lead',
            specialization: ['Milking', 'Animal Health', 'Feed Planning'],
          },
          {
            id: 'tutorial-worker-sam',
            name: 'Samuel G.',
            role: 'Livestock Assistant',
            specialization: ['Barn Hygiene', 'Fodder Handling', 'Calf Care'],
          },
        ],
        tasks: [
          {
            id: 'tutorial-task-milking',
            title: 'Prepare the morning milking station',
            description: 'Sanitize milking equipment, confirm cooling containers are ready, and log any abnormalities before the session starts.',
            category: 'Animal Care',
            priority: 'urgent',
            status: 'pending',
            frequency: 'daily',
            timeSlots: ['05:30'],
            isMandatory: true,
            dueDate: daysFromNow(0),
            assignedTo: [],
            checklist: [
              { id: 'tutorial-milking-1', text: 'Sanitize collection equipment', completed: false },
              { id: 'tutorial-milking-2', text: 'Inspect cooling containers', completed: false },
            ],
            notes: 'Tutorial example showing a recurring high-priority dairy workflow.',
            createdAt: daysAgo(1),
            updatedAt: daysAgo(1),
            assignedWorkers: ['tutorial-worker-mary'],
          },
          {
            id: 'tutorial-task-fodder',
            title: 'Inspect fodder reserves for the next seven days',
            description: 'Check silage, napier, and supplementary feed volumes so procurement gaps are identified early.',
            category: 'General',
            priority: 'high',
            status: 'in-progress',
            frequency: 'weekly',
            isMandatory: true,
            dueDate: daysFromNow(3),
            assignedTo: [],
            checklist: [
              { id: 'tutorial-fodder-1', text: 'Check silage pit levels', completed: true },
              { id: 'tutorial-fodder-2', text: 'Review napier cutting plan', completed: false },
            ],
            notes: 'Demonstrates planning around feed security rather than one-off tasks.',
            createdAt: daysAgo(4),
            updatedAt: daysAgo(1),
            assignedWorkers: ['tutorial-worker-sam'],
          },
        ],
      },
      weather: {
        summary: 'This tutorial weather plan focuses on fodder growth, animal comfort, and disease prevention for a dairy farm in Kiambu.',
        planningNotes: [
          'Track wet spells because muddy housing conditions increase animal stress and hygiene risks.',
          'Use temperature swings to plan water access and shade around peak afternoon heat.',
          'Pair rainfall expectations with napier harvest timing so fodder does not become overmature.',
        ],
        cropRecommendations: [
          {
            name: 'Napier Grass',
            suitability: 'high',
            notes: 'Fits the dairy tutorial profile well when cutting intervals and moisture management stay consistent.',
          },
          {
            name: 'Fodder Maize',
            suitability: 'high',
            notes: 'Useful for silage planning when planting and harvest dates are coordinated with expected rains.',
          },
          {
            name: 'Lucerne',
            suitability: 'medium',
            notes: 'Can diversify feed quality, but requires stronger establishment and moisture monitoring.',
          },
        ],
        pestAlerts: [
          {
            name: 'Tick Pressure',
            risk: 'medium',
            crops: ['Livestock Housing'],
            prevention: 'Keep housing dry, inspect animals routinely, and maintain the spray program on schedule.',
          },
          {
            name: 'Fodder Fungal Spoilage',
            risk: 'medium',
            crops: ['Napier Grass', 'Stored Silage'],
            prevention: 'Avoid wet storage conditions and rotate older fodder lots out first.',
          },
        ],
      },
      produce: {
        summary: 'This tutorial produce plan shows how a dairy farm can think about milk output, storage discipline, and market-readiness before recording real production data.',
        harvestTargets: [
          {
            name: 'Raw Milk',
            unit: 'litres',
            target: '480',
            notes: 'Use daily collection logs and cooling records before aggregating weekly sales.',
          },
          {
            name: 'Napier Fodder Bundles',
            unit: 'bundles',
            target: '55',
            notes: 'Plan for on-farm use first, then record any surplus for sale separately.',
          },
        ],
        marketingChecklist: [
          'Separate farm-consumption records from milk sold to buyers.',
          'Document storage and cooling readiness before marketing perishable output.',
          'Track daily yield trends so low-output periods can be investigated quickly.',
        ],
      },
      workforce: {
        summary: 'This tutorial workforce view shows how dairy routines rely on role clarity, repetition, and hygiene controls more than ad hoc daily labor.',
        playbook: [
          'Assign clear ownership for milking, hygiene, and feed checks across the week.',
          'Use shift overlap when milk collection and cleaning happen close together.',
          'Build simple training routines around animal health observations and hygiene logging.',
        ],
      },
    },
  },
];

export const isTutorialFarmId = (farmId?: string | null) =>
  typeof farmId === 'string' && farmId.startsWith('tutorial-');

export const getTutorialFarmById = (farmId?: string | null) =>
  tutorialFarms.find((farm) => farm.id === farmId) ?? null;
